import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck, Zap } from "lucide-react";
import { Phone, PhoneBadge } from "./Phone";
import { HERO_SCRIPT } from "../../data/content";

const LINES = ["Your Business Shouldn't", "Have to Chase", "Every Lead."];

const STATS = [
    { value: "8 sec", label: "Avg. response time" },
    { value: "24/7", label: "Always on duty" },
    { value: "0", label: "Leads left behind" },
];

export default function Hero() {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, -2]), { stiffness: 60, damping: 16 });
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -2]), { stiffness: 60, damping: 16 });

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20 sm:pt-32" onMouseMove={onMouseMove} data-testid="hero-section">
            <div className="absolute inset-0 bg-grid" />
            <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 blur-[140px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-600/10 blur-[140px] rounded-full pointer-events-none" />

            <div className="container-x relative grid lg:grid-cols-2 gap-16 lg:gap-8 items-center">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-8"
                        data-testid="hero-eyebrow"
                    >
                        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                        <span className="ff-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-cyan-300">Your AI Employee · Online Now</span>
                    </motion.div>

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white" data-testid="hero-headline">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.13, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                                    className={`block ${i === 2 ? "text-gradient" : ""}`}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl"
                        data-testid="hero-subheadline"
                    >
                        Meet your AI-powered employee that answers inquiries, follows up with leads, books appointments, and keeps your business moving — 24/7.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a href="#demo" className="btn-primary" data-testid="hero-see-how-it-works-button">
                            See How It Works
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <a href="#book" className="btn-ghost" data-testid="hero-book-demo-button">
                            Start a Chat Interview
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-14 flex gap-10 sm:gap-14"
                        data-testid="hero-stats"
                    >
                        {STATS.map((s) => (
                            <div key={s.label}>
                                <p className="ff-display text-2xl sm:text-3xl font-bold text-white">{s.value}</p>
                                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative flex justify-center lg:justify-end" style={{ perspective: 1400 }}>
                    <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
                        <Phone script={HERO_SCRIPT} testid="hero-phone" />
                        <PhoneBadge icon={CalendarCheck} title="Appointment Booked ✓" sub="Thursday · 10:00 AM" className="-top-6 -right-4 sm:-right-10" delay={1.2} />
                        <PhoneBadge icon={Zap} title="Lead Responded in 8 sec" sub="While you were busy" className="bottom-24 -left-4 sm:-left-14" delay={1.5} />
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
            >
                <span className="ff-mono text-[9px] uppercase tracking-[0.25em] text-slate-600">Scroll</span>
                <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }} className="w-px h-8 bg-gradient-to-b from-cyan-400/60 to-transparent" />
            </motion.div>
        </section>
    );
}
